import axios from "axios";
import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, FormControlLabel, Switch, Stack } from '@mui/material';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import createActivityImg from '../assets/undraw_creative_thinking_re_9k71.svg';
import url from '../url.js';
import ActivitySettingsDialog from './Admin/ActivitySettingsDialog';
import RoomSettingsChatroomIcon from "../assets/RoomSettings/chatroom.png";
import RoomSettingsMindmapIcon from "../assets/RoomSettings/mindmap.png";
import ForumIcon from "../assets/返回論壇icon.png";
import ActivityInfo from '../models/ActivityInfo';
import { normalizeSettingsForInitialNodes } from '../utils/activityInfoAccessors';
import { useTranslation } from 'react-i18next';

export const CreateActivityForm = ({ callback_setActivities, renderTrigger }) => {
    const { t } = useTranslation('main');
    const userId = useMemo(() => localStorage.getItem('userId'), []);
    const [loading, setLoading] = useState(false); 
    const [open, setOpen] = useState(false);
    const [openSettingOfActivity, setOpenSettingOfActivity] = useState(false);
    // ❌ 移除 roomSetting - 由對話框內部管理
    const [activityIdForSettings, setActivityIdForSettings] = useState(null);
    const [data, setData] = useState({
        title: "",
        description: "",
        userId: userId,
        startDate: dayjs(),
        endDate: dayjs().add(1, 'month'),
        isPublic: true
    });

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        // 重置表單數據為初始值
        setData({
            title: "",
            description: "",
            userId: userId,
            startDate: dayjs(),
            endDate: dayjs().add(1, 'month'),
            isPublic: true
        });
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setData({
            ...data,
            [e.target.name]: value
        });
    };

    const handleDateChange = (name, newDate) => {
        setData({
            ...data,
            [name]: newDate
        });
    };

    // ❌ 移除不再需要的函數（由對話框內部處理）

    const initializeActivitySettings = async (activityId) => {
        // ✅ 只需設置 ID 並開啟對話框
        setActivityIdForSettings(activityId);
            setOpenSettingOfActivity(true);
    };

    // 重新獲取活動列表的函數
    const refreshActivityList = async () => {
        try {
            console.log('重新獲取活動列表...');
            const jwtToken = localStorage.getItem('jwtToken');
            const headers = { Authorization: `Bearer ${jwtToken}` };
            const [joinedRes, createdRes] = await Promise.all([
                axios.get(`${url.backendHost}api/activities/myJoined/${userId}`, { 
                    headers, 
                    withCredentials: true 
                }),
                axios.get(`${url.backendHost}api/activities/myActivity/${userId}`, { 
                    headers, 
                    withCredentials: true 
                })
            ]);

            const joined = Array.isArray(joinedRes.data) ? joinedRes.data : [];
            const created = Array.isArray(createdRes.data) ? createdRes.data : [];

            const byId = new Map();

            // 處理創建的活動（擁有者視圖）
            for (const act of created) {
                const id = act.id;
                const existing = byId.get(id) || { 
                    activity: act, 
                    joinedGroups: [], 
                    allGroups: [], 
                    isOwner: true 
                };
                existing.activity = act;
                existing.isOwner = true;
                existing.allGroups = Array.isArray(act.Groups) ? act.Groups : existing.allGroups;
                if (existing.isOwner && existing.allGroups.length > 0) {
                    existing.joinedGroups = existing.allGroups.slice();
                }
                byId.set(id, existing);
            }

            // 處理參與的活動
            for (const entry of joined) {
                const act = entry?.ActivityGroup?.Activity;
                const grp = entry?.ActivityGroup?.Group;
                if (!act || !grp) continue;
                
                const id = act.id;
                const existing = byId.get(id) || { 
                    activity: act, 
                    joinedGroups: [], 
                    allGroups: [], 
                    isOwner: act.userId === Number(userId) 
                };
                existing.activity = act;
                existing.joinedGroups = existing.joinedGroups || [];
                if (!existing.joinedGroups.find(g => g.id === grp.id)) {
                    existing.joinedGroups.push(grp);
                }
                byId.set(id, existing);
            }

            const merged = Array.from(byId.values())
                .sort((a, b) => new Date(b.activity.createdAt) - new Date(a.activity.createdAt));
            
            console.log('活動列表更新成功，共', merged.length, '個活動');
            callback_setActivities(merged);
        } catch (error) {
            console.error('重新獲取活動列表失敗:', error);
            // 如果重新獲取失敗，記錄錯誤但不修改活動列表
            // 用戶可以手動刷新頁面來查看新活動
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const jwtToken = localStorage.getItem("jwtToken");
        const headers = { Authorization: `Bearer ${jwtToken}` };
        const activityData = {
            userId: data.userId,
            title: data.title,
            content: data.description,
            startDate: data.startDate.toISOString(),
            endDate: data.endDate.toISOString(),
            isPublic: data.isPublic
        };
        try {
            const activityResponse = await axios.post(`${url.backendHost}api/activities/create`, activityData, { 
                headers, 
                withCredentials: true 
            });
            const newActivity = activityResponse.data;
            localStorage.setItem('activityId', newActivity.id);
            sessionStorage.setItem('activityId', newActivity.id);
            console.log('活動創建成功:', newActivity);
            
            // 更新 token 以包含新活動的權限
            const renewResponse = await axios.get(`${url.backendHost}api/users/renew`, { 
                headers, 
                withCredentials: true 
            });
            
            // 更新 localStorage 中的 jwtToken
            if (renewResponse.data.jwtToken) {
                localStorage.setItem('jwtToken', renewResponse.data.jwtToken);
                // 更新后续请求的 headers
                headers.Authorization = `Bearer ${renewResponse.data.jwtToken}`;
                console.log('活動創建後 JWT token 已更新');
            } else {
                console.warn('警告：無法獲取新的 JWT token，可能會導致權限問題');
            }
            
            // 等待一小段時間確保token更新完成
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const groupData = {
                groupName: "第1組",
                activityId: newActivity.id,
                numGroups: 1
            };
            
            console.log('開始創建小組:', groupData);
            
            // 嘗試創建小組，如果權限失敗則重試一次
            let groupResponse;
            try {
                groupResponse = await axios.post(`${url.backendHost}api/activities/createGroups`, groupData, { 
                    headers, 
                    withCredentials: true 
                });
            } catch (groupError) {
                if (groupError.response?.status === 403) {
                    console.log('權限不足，重新更新 token 後重試...');
                    // 再次更新 token
                    const retryRenewResponse = await axios.get(`${url.backendHost}api/users/renew`, { 
                        headers, 
                        withCredentials: true 
                    });
                    
                    if (retryRenewResponse.data.jwtToken) {
                        localStorage.setItem('jwtToken', retryRenewResponse.data.jwtToken);
                        headers.Authorization = `Bearer ${retryRenewResponse.data.jwtToken}`;
                        console.log('重試：JWT token 已更新');
                    }
                    
                    // 等待更長時間確保 token 更新完成
                    await new Promise(resolve => setTimeout(resolve, 200));
                    
                    // 重試創建小組
                    groupResponse = await axios.post(`${url.backendHost}api/activities/createGroups`, groupData, { 
                        headers, 
                        withCredentials: true 
                    });
                } else {
                    throw groupError;
                }
            }

            console.log('小組創建成功:', groupResponse.data);
            const joinCode = groupResponse.data.groups[0].joinCode;
            const joinData = { userId: data.userId };

            console.log('開始加入小組:', joinCode);
            await axios.put(`${url.backendHost}api/groups/${joinCode}/join`, joinData, { 
                headers, 
                withCredentials: true 
            });

            // 加入小組後，再次更新 token 以包含小組權限
            console.log('加入小組後更新 token 以包含小組權限...');
            const finalRenewResponse = await axios.get(`${url.backendHost}api/users/renew`, { 
                headers, 
                withCredentials: true 
            });
            
            if (finalRenewResponse.data.jwtToken) {
                localStorage.setItem('jwtToken', finalRenewResponse.data.jwtToken);
                console.log('最終 JWT token 已更新，包含小組權限');
            }

            // 將創建的小組信息添加到活動對象中
            newActivity.Groups = groupResponse.data.groups;
            setOpen(false);
            setData({
                title: "",
                description: "",
                userId: userId,
                startDate: dayjs(),
                endDate: dayjs().add(1, 'month'),
                isPublic: true
            });
            
            // 重新獲取完整的活動列表以確保包含最新的權限信息
            await refreshActivityList();
            
            // 初始化活動設定
            await initializeActivitySettings(newActivity.id);
        } catch (error) {
            console.error("建立活動或小組失敗:", error.response || error.message);
            alert(t('createActivity.failAlert'));
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div>
            {renderTrigger ? (
                renderTrigger(handleClickOpen)
            ) : (
                <div onClick={handleClickOpen} style={{ cursor: 'pointer', color: 'black', textDecoration: 'none' }}>
                    {t('createActivity.trigger')}
                </div>
            )}
            <Dialog open={open} onClose={handleClose}>
                <div>
                    <img className='modal-image' src={createActivityImg} alt="Create Activity" />
                </div>
                <DialogTitle sx={{ color: 'black', textDecoration: 'none' }}>{t('createActivity.title')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={data.isPublic}
                                    onChange={(e) => setData({ ...data, isPublic: e.target.checked })}
                                />
                            }
                            label={data.isPublic ? '公開活動' : '私人活動'}
                        />
                    </Stack>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="title"
                        label={t('createActivity.placeholder')}
                        type="text"
                        name='title'
                        value={data.title}
                        fullWidth
                        variant="standard"
                        onChange={handleChange}
                    />
                    <TextField
                        margin="dense"
                        id="description"
                        label="活動說明"
                        name='description'
                        value={data.description}
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        onChange={handleChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t('createActivity.cancel')}</Button>
                    <Button onClick={handleSubmit}>{t('createActivity.add')}</Button>
                </DialogActions>
            </Dialog>

            <ActivitySettingsDialog
                open={openSettingOfActivity}
                onClose={() => {
                    setOpenSettingOfActivity(false);
                }}
                activityId={activityIdForSettings}
            />
        </div>
    );
};
